import { useMemo } from 'react';

import { isFeatureEnabled } from '@website/config/featureFlags';
import { Activity } from '@website/services/activities.service';
import { calculateClusterThreshold, calculateDistance } from '@website/utils/mapUtils';

interface UseFilteredActivitiesParams {
  activities?: Activity[];
  zoom?: number | null;
}

export function useFilteredActivities({ activities, zoom }: UseFilteredActivitiesParams) {
  const allActivities = useMemo(() => activities || [], [activities]);

  const ratingEnabled = isFeatureEnabled('RATING');

  const activitiesForListingCards = useMemo(() => {
    return allActivities.map((act: Activity) => ({
      id: act.id,
      title: act.title,
      imageUrl: act.imageUrl,
      professionalName: act.professionalName,
      isAtHome: act.isAtHome,
      startingPrice: act.startingPrice,
      sessionsSinceLastYear: act.sessionsSinceLastYear || 0,
      city: act.location?.address,
      ratingAverage: ratingEnabled ? act.rating?.ratingAverage : undefined,
      ratingTotal: ratingEnabled ? act.rating?.ratingTotal : undefined
    }));
  }, [allActivities, ratingEnabled]);

  const atHomeActivities = useMemo(() => {
    return allActivities
      .filter((activity) => activity.isAtHome)
      .map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD', // TODO: Get from activity if available
        imageUrl: a.imageUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }));
  }, [allActivities]);

  const mapLocations = useMemo(() => {
    const activitiesWithLocations = allActivities.filter(
      (activity) => activity.location && activity.location.coordinates && !activity.isAtHome
    );

    const clusterThreshold = calculateClusterThreshold(zoom);
    const useExactMatching = clusterThreshold < 0.01;

    if (useExactMatching) {
      const locationGroups = new Map<string, Activity[]>();

      activitiesWithLocations.forEach((activity: Activity) => {
        const lat = activity.location.coordinates.latitude;
        const lng = activity.location.coordinates.longitude;
        const locationKey = `${lat},${lng}`;

        if (!locationGroups.has(locationKey)) {
          locationGroups.set(locationKey, []);
        }
        locationGroups.get(locationKey)!.push(activity);
      });

      const markers = Array.from(locationGroups.entries()).map(([locationKey, activities]) => {
        const firstActivity = activities[0];
        const lat = firstActivity.location.coordinates.latitude;
        const lng = firstActivity.location.coordinates.longitude;

        if (activities.length > 1) {
          return {
            id: locationKey,
            latitude: lat,
            longitude: lng,
            name: `${activities.length} activities`,
            count: activities.length,
            activityIds: activities.map((a) => a.id),
            activities: activities.map((a) => ({
              id: a.id,
              title: a.title,
              professionalName: a.professionalName,
              startingPrice: a.startingPrice,
              currency: 'CAD',
              imageUrl: a.imageUrl,
              isAtHome: a.isAtHome,
              homeRadius: a.homeRadius
            }))
          };
        }

        return {
          id: firstActivity.id,
          latitude: lat,
          longitude: lng,
          name: firstActivity.title,
          isAtHome: firstActivity.isAtHome,
          homeRadius: firstActivity.homeRadius
        };
      });

      return markers;
    }

    const clusters: Array<{
      activities: Activity[];
      centerLat: number;
      centerLng: number;
    }> = [];
    const processed = new Set<string>();

    activitiesWithLocations.forEach((activity: Activity) => {
      if (processed.has(activity.id)) {
        return;
      }

      const nearbyActivities: Activity[] = [activity];
      processed.add(activity.id);

      let changed = true;
      while (changed) {
        changed = false;

        const currentCenterLat =
          nearbyActivities.reduce((sum, a) => sum + a.location.coordinates.latitude, 0) / nearbyActivities.length;
        const currentCenterLng =
          nearbyActivities.reduce((sum, a) => sum + a.location.coordinates.longitude, 0) / nearbyActivities.length;

        activitiesWithLocations.forEach((otherActivity: Activity) => {
          if (processed.has(otherActivity.id)) {
            return;
          }

          const otherLat = otherActivity.location.coordinates.latitude;
          const otherLng = otherActivity.location.coordinates.longitude;

          const distance = calculateDistance(currentCenterLat, currentCenterLng, otherLat, otherLng);

          if (distance <= clusterThreshold) {
            nearbyActivities.push(otherActivity);
            processed.add(otherActivity.id);
            changed = true;
          }
        });
      }

      const centerLat =
        nearbyActivities.reduce((sum, a) => sum + a.location.coordinates.latitude, 0) / nearbyActivities.length;
      const centerLng =
        nearbyActivities.reduce((sum, a) => sum + a.location.coordinates.longitude, 0) / nearbyActivities.length;

      clusters.push({
        activities: nearbyActivities,
        centerLat,
        centerLng
      });
    });

    const markers = clusters.map((cluster) => {
      if (cluster.activities.length > 1) {
        const clusterId = `cluster_${cluster.centerLat.toFixed(6)}_${cluster.centerLng.toFixed(6)}`;
        return {
          id: clusterId,
          latitude: cluster.centerLat,
          longitude: cluster.centerLng,
          name: `${cluster.activities.length} activities`,
          count: cluster.activities.length,
          activityIds: cluster.activities.map((a) => a.id),
          activities: cluster.activities.map((a) => ({
            id: a.id,
            title: a.title,
            professionalName: a.professionalName,
            startingPrice: a.startingPrice,
            currency: 'CAD',
            imageUrl: a.imageUrl,
            isAtHome: a.isAtHome,
            homeRadius: a.homeRadius
          }))
        };
      }

      const activity = cluster.activities[0];
      return {
        id: activity.id,
        latitude: activity.location.coordinates.latitude,
        longitude: activity.location.coordinates.longitude,
        name: activity.title,
        isAtHome: activity.isAtHome,
        homeRadius: activity.homeRadius
      };
    });

    return markers;
  }, [allActivities, zoom]);

  return {
    activitiesForListingCards,
    mapLocations,
    atHomeActivities
  };
}
